import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { tokenStorage } from '../lib/auth'
import { api } from '../lib/api'
import i18n from '../features/i18n'
import type { User } from '../types/user'

interface LoginPayload {
  email: string
  password: string
  /** '로그인 유지' — 미선택이면 서버가 세션 쿠키로 내려 브라우저 종료 시 로그아웃된다. */
  remember?: boolean
}

// 백엔드 /api/auth/login 응답 형식:
//   { access_token, token_type, expires_in }
// refresh_token 은 HttpOnly 쿠키로 별도 설정됨. user 정보는 /auth/me 로 별도 조회.
interface LoginResponse {
  access_token: string
  token_type?: string
  expires_in?: number
}

export function useAuth() {
  const { user, isAuthenticated, setUser, logout: storeLogout } = useAuthStore()
  const navigate = useNavigate()

  const login = useCallback(async (payload: LoginPayload) => {
    console.debug('[useAuth] login 시작', payload.email)
    const { data } = await api.post<LoginResponse>('/auth/login', payload)
    console.debug('[useAuth] login 응답 OK', { access_token_len: data.access_token?.length })
    tokenStorage.setAccess(data.access_token)
    // 로그인 직후 CSRF 토큰 갱신 (이후 POST/PATCH 에 필요)
    try {
      await api.get('/auth/csrf')
    } catch { /* 무시 — 나중에 필요 시 retry */ }
    // 사용자 정보는 access token 으로 별도 조회
    try {
      const { data: me } = await api.get<User>('/auth/me')
      setUser(me)
      // 백엔드 저장 locale 을 i18n 에 반영 (다른 기기에서 바꾼 경우 동기화)
      if (me?.locale && me.locale !== i18n.language) {
        try { await i18n.changeLanguage(me.locale) } catch { /* noop */ }
      }
    } catch (e) {
      console.warn('[useAuth] /auth/me 조회 실패, 계속 진행', e)
    }
    // 사업장이 등록된 계정은 사업장 관리 페이지를 메인으로 — 등록된 사업장으로 바로 이동.
    try {
      const { data: sites } = await api.get<Array<{ id: string }>>('/sites')
      if (Array.isArray(sites) && sites.length > 0) {
        navigate(`/dashboard/site/${sites[0].id}`, { replace: true })
        return
      }
    } catch { /* 사업장 조회 실패 시 기본 대시보드로 */ }
    navigate('/dashboard')
  }, [setUser, navigate])

  const logout = useCallback(() => {
    tokenStorage.clearAll()
    storeLogout()
    navigate('/login')
  }, [storeLogout, navigate])

  const fetchCurrentUser = useCallback(async () => {
    try {
      const { data } = await api.get<User>('/auth/me')
      setUser(data)
      if (data?.locale && data.locale !== i18n.language) {
        try { await i18n.changeLanguage(data.locale) } catch { /* noop */ }
      }
      return data
    } catch {
      logout()
      return null
    }
  }, [setUser, logout])

  return {
    user,
    isAuthenticated,
    login,
    logout,
    fetchCurrentUser,
  }
}
